<template>
    <div class="survey-review-link">
        <div
            v-if="!showGoReviewButton"
            class="survey-review-link--before-click"
        >
            <img :src="ReviewGreenExclamation" alt="" role="presentation" />
            <Typography variant="body1">{{ requestTitle }}</Typography>
            <div
                v-if="aiSummaryLoading"
                class="survey-review-link--summary-loading"
                :class="{ active: aiSummaryLoading }"
            >
                <span data-testid="loadingDot" class="dot dot-1"> </span>
                <span data-testid="loadingDot" class="dot dot-2"></span>
                <span data-testid="loadingDot" class="dot dot-3"></span>
            </div>
            <div
                v-if="!aiSummaryLoading"
                class="survey-review-link--survey-summary"
            >
                <Typography v-if="aiSummary" variant="body1" class="bold">We have summarized your answers with AI into the
                    following:</Typography>
                <div class="survey-review-link--survey-summary--text">
                    <Typography variant="body2">"{{
                            aiSummary ? aiSummary : reviewComment
                        }}"</Typography>
                </div>
            </div>
            <button
                v-if="!aiSummaryLoading"
                class="survey-review-link--button"
                :data-url="item.link"
                :aria-label="`Copy comment. Will automatically navigate in ${countdownSeconds} seconds after clicking`"
                type="button"
                :disabled="aiSummaryLoading"
                @click="copyComment(item.link)"
            >
                <img :src="ReviewCopyIcon" alt="" role="presentation" />
                <Typography variant="body2">{{ btnTitle }}</Typography>
            </button>
        </div>
        <div v-if="showGoReviewButton" class="survey-review-link--after-click">
            <img :src="ReviewGreenCheck" alt="" role="presentation" />
            <Typography variant="body1">{{ confirmationMessage }}</Typography>
            <button
                class="survey-review-link--button"
                :data-url="item.link"
                :aria-label="`Go now. Will automatically navigate in ${countdownSeconds} seconds`"
                type="button"
                @click="visitReviewPage(item.link)"
            >
                <img :src="ReviewGoIcon" alt="" role="presentation" />
                <Typography variant="body2">GO NOW</Typography>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
// customer face survey form component
import { onMounted, ref } from 'vue'
import { ReceiveText } from '@/entities/conversation'
import Typography from '@/components/Rain/Typography/Typography.vue'
import ReviewCopyIcon from '@/assets/img/Icons/White/review-copy.svg'
import ReviewGoIcon from '@/assets/img/Icons/White/review-go.svg'
import ReviewGreenCheck from '@/assets/img/Icons/Green/review-check.svg'
import ReviewGreenExclamation from '@/assets/img/Icons/Green/review-exclamation.svg'
import { copyToClipboard } from '@/utils/function'
import {
    getAiSurveyReviewSummary,
    trackReviewLinkClicked,
} from '@/api/conversation'
import {
    SurveyDisplayValues,
    REVIEW_LINK_DEFAULT_TITLE,
    REVIEW_LINK_DEFAULT_BTN_TITLE,
    REVIEW_LINK_DEFAULT_CONFIRM_MESSAGE,
} from '@/entities/conversation'
interface Props {
    item: ReceiveText
    surveyDisplayValues?: SurveyDisplayValues
    slug: string
}
const props = defineProps<Props>()

const showGoReviewButton = ref(false)
const countdownSeconds = ref(3)
const shortlinkSlug = ref('')
const requestTitle = ref(props.item.title || REVIEW_LINK_DEFAULT_TITLE)
const btnTitle = ref(props.item.btnTitle || REVIEW_LINK_DEFAULT_BTN_TITLE)
const confirmationMessage = ref(
    props.item.confirmationMessage || REVIEW_LINK_DEFAULT_CONFIRM_MESSAGE
)
const reviewComment = ref('')
const aiSummaryLoading = ref(false)
const aiSummary = ref('')

const emit = defineEmits(['callNext'])

onMounted(async () => {
    reviewComment.value = props.surveyDisplayValues?.comment as string

    if (hasDynamicSurvey()) {
        aiSummaryLoading.value = true
    }

    setTimeout(() => {
        // wait for transitions to complete
        emit('callNext')
    }, 500)

    if (hasDynamicSurvey()) {
        try {
            const { data: aiSurveySummaryData } =
                await getAiSurveyReviewSummary(props.slug)
            aiSummary.value = aiSurveySummaryData?.result?.summary || ''
        } catch {
            // nothing to do
        }

        aiSummaryLoading.value = false
    }

    const { data: shortLinkData } = await trackReviewLinkClicked(
        props.slug,
        props.item.link,
        false,
        undefined
    )
    shortlinkSlug.value = shortLinkData.linkSlug
})

const hasDynamicSurvey = () => {
    return Object.keys(props.surveyDisplayValues || {}).some((key) =>
        key.startsWith('ai_question_')
    )
}

const copyComment = (link: string | undefined) => {
    if (aiSummary.value) {
        copyToClipboard(aiSummary.value)
    } else {
        copyToClipboard(reviewComment.value)
    }

    showGoReviewButton.value = true

    const timer = setInterval(function () {
        if (countdownSeconds.value <= 1) {
            clearInterval(timer)
            visitReviewPage(link)
        }
        countdownSeconds.value--
    }, 1000)
}

const visitReviewPage = (link: string | undefined) => {
    window.open(link, '_blank')
    trackReviewLinkClicked(props.slug, link, true, shortlinkSlug.value)
}
</script>

<style scoped lang="less">
@import '~@/styles/palette.less';
@import '~@/styles/rain/variables.less';

.survey-review-link {
    display: flex;
    justify-content: center;
    flex-direction: column;
    margin-top: 40px;
    background-color: #ffffff;
    position: relative;

    &--before-click,
    &--after-click {
        width: 300px;
        text-align: center;
        margin: 10px auto;

        p.body1 {
            text-align: center;
            width: 200px;
            margin: 0px auto;
        }
    }

    &--button {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        width: 230px;
        background-color: #4394d3;
        border-radius: @borderRadius-md;
        border: 1px solid #4394d3;
        padding: 14px 10px;
        gap: 8px;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        //color: @white;
        margin: 20px auto 0;
        cursor: pointer;

        p {
            color: @white;
        }
    }

    &--survey-summary {
        display: flex;
        flex-direction: column;
        gap: 14px;
        margin-top: 14px;

        &--text {
            border: 1px solid #4394d3;
            border-radius: @borderRadius-md;
            padding: 10px;

            p {
                color: #4394d3;
            }
        }
    }

    &--summary-loading {
        margin-top: 14px;

        &.active {
            border: 1px solid #fff;
            color: #fff;
        }

        .dot {
            width: 10px;
            height: 10px;
            display: inline-block;
            border-radius: 10px;
            margin: 7px 5px 0px 5px;
            background-color: var(--brandColor);
        }
        .dot-1 {
            animation: jump 1s ease-in-out 0s infinite;
        }
        .dot-2 {
            animation: jump 1s ease-in-out 100ms infinite;
        }
        .dot-3 {
            animation: jump 1s ease-in-out 200ms infinite;
        }
    }
}

.bold {
    font-weight: 600;
}
</style>
